import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Bot, Send, Loader2, X, Sparkles, Share2, BarChart3, CalendarPlus, Home } from "lucide-react";
import { crmCopilot, generateContent, getCrmPulse, bookViewing, addCalendarEvent, fetchProperties } from "../api";

const QUICK = [
  "Show my hottest leads",
  "Which leads are going stale?",
  "How many leads are in the CRM?",
];

const AiDock = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [chat, setChat] = useState([{ role: "bot", text: "Hi — I'm your Anadear AI. I can pull hot leads, chase stale ones, draft emails or answer questions. What shall I do?" }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [panel, setPanel] = useState(null); // 'viewing' | 'event'
  const [props, setProps] = useState([]);
  const [vf, setVf] = useState({ propertyId: "", name: "", email: "", phone: "", preferredDate: "" });
  const [ef, setEf] = useState({ title: "", date: "", time: "09:00", type: "task" });
  const [saving, setSaving] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { if (open) endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [chat, open]);
  useEffect(() => {
    if (panel === "viewing" && props.length === 0) {
      fetchProperties({ sort: "newest" }).then((d) => {
        const seen = new Set();
        setProps((d || []).filter((p) => (seen.has(p.id) ? false : seen.add(p.id))));
      }).catch(() => {});
    }
  }, [panel]); // eslint-disable-line

  const say = (text) => setChat((c) => [...c, { role: "bot", text }]);

  const submitViewing = async (e) => {
    e.preventDefault();
    if (!vf.propertyId || !vf.name || !vf.email) return;
    setSaving(true);
    try {
      const p = props.find((x) => x.id === vf.propertyId);
      await bookViewing({ ...vf, propertyTitle: p?.title });
      setPanel(null); setVf({ propertyId: "", name: "", email: "", phone: "", preferredDate: "" });
      say(`📅 Viewing booked for ${vf.name} at ${p?.title || "the property"}${vf.preferredDate ? ` on ${vf.preferredDate}` : ""}. It's in Viewings and the CRM.`);
    } catch { say("Couldn't book that viewing — please try again."); }
    finally { setSaving(false); }
  };

  const submitEvent = async (e) => {
    e.preventDefault();
    if (!ef.title || !ef.date) return;
    setSaving(true);
    try {
      await addCalendarEvent({ title: ef.title, date: `${ef.date}T${ef.time || "09:00"}:00`, type: ef.type });
      setPanel(null); setEf({ title: "", date: "", time: "09:00", type: "task" });
      say(`🗓️ Added “${ef.title}” to your calendar.`);
    } catch { say("Couldn't add that event — please try again."); }
    finally { setSaving(false); }
  };

  const ask = async (text) => {
    const q = (text || input).trim();
    if (!q) return;
    setChat((c) => [...c, { role: "me", text: q }]);
    setInput(""); setBusy(true);
    try {
      const r = await crmCopilot(q);
      let reply = r.reply || "Done.";
      if (r.result?.leads) reply += "\n\n" + r.result.leads.slice(0, 8).map((l) => `• ${l.name} (${l.temperature || "—"}, score ${l.score ?? "-"})`).join("\n");
      if (r.result?.total != null) reply += `\n\nTotal leads: ${r.result.total}.`;
      if (r.result?.summary) reply += `\n\n${r.result.summary}`;
      if (r.result?.pendingBulkEmail) reply += `\n\n📧 ${r.result.pendingBulkEmail.matched} contact(s) match. Open the CRM Email tool to send.`;
      setChat((c) => [...c, { role: "bot", text: reply }]);
    } catch { setChat((c) => [...c, { role: "bot", text: "Sorry — something went wrong. Try again." }]); }
    finally { setBusy(false); }
  };

  const draftSocial = async () => {
    const topic = input.trim() || "our latest Bath & Bristol listings and why now is a great time to move";
    setChat((c) => [...c, { role: "me", text: `Draft a social post: ${topic}` }]);
    setInput(""); setBusy(true);
    try {
      await generateContent({ type: "post", platform: "instagram", prompt: topic });
      setChat((c) => [...c, { role: "bot", text: "✍️ Drafted an Instagram post and saved it to Marketing → Pending Review. Approve it there and publish to Facebook/Instagram/LinkedIn." }]);
    } catch { setChat((c) => [...c, { role: "bot", text: "Couldn't draft that post — please try again." }]); }
    finally { setBusy(false); }
  };

  const listingStats = async () => {
    setChat((c) => [...c, { role: "me", text: "Show me live listing stats" }]);
    setBusy(true);
    try {
      const p = await getCrmPulse();
      const text = `📊 Live pulse:\n• ${p.listings} listings on site (${p.forSale} for sale · ${p.toRent} to rent)\n• ${p.appSynced} synced from the app\n• ${p.leads} leads (${p.hotLeads} hot)\n• ${p.viewingsToday} viewing(s) today`;
      setChat((c) => [...c, { role: "bot", text }]);
    } catch { setChat((c) => [...c, { role: "bot", text: "Couldn't fetch stats — please try again." }]); }
    finally { setBusy(false); }
  };

  return (
    <>
      {open && (
        <div className="fixed bottom-24 right-6 z-50 w-[380px] max-w-[calc(100vw-2rem)] h-[520px] max-h-[72vh] bg-white rounded-2xl shadow-[0_24px_70px_rgba(0,0,0,0.28)] border border-[#e7dcc9] flex flex-col overflow-hidden" data-testid="ai-dock-panel">
          <div className="bg-gradient-to-r from-[#1c1c1e] to-[#2b2b2b] px-4 py-3.5 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <span className="w-8 h-8 rounded-lg bg-[#b8935f]/25 flex items-center justify-center"><Sparkles size={16} className="text-[#d9b98a]" /></span>
              <div><p className="text-white text-[14px] font-500 leading-none">Anadear AI</p><p className="text-white/50 text-[11px] mt-1">Always on · acts on your CRM</p></div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="ai-dock-close" className="text-white/70 hover:text-white transition-colors"><X size={18} /></button>
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 bg-[#faf7f2]">
            {chat.map((m, i) => (
              <div key={i} className={`flex ${m.role === "me" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-3.5 py-2 rounded-2xl text-[13px] whitespace-pre-wrap leading-relaxed ${m.role === "me" ? "bg-[#1c1c1e] text-white" : "bg-white text-[#3a3a3a] border border-[#f0ece5]"}`}>{m.text}</div>
              </div>
            ))}
            {busy && <div className="flex justify-start"><div className="px-3.5 py-2 rounded-2xl bg-white border border-[#f0ece5]"><Loader2 size={15} className="animate-spin text-[#b8935f]" /></div></div>}
            {chat.length <= 1 && (
              <div className="flex flex-wrap gap-2 pt-1">
                {QUICK.map((q) => (
                  <button key={q} onClick={() => ask(q)} className="text-[12px] px-3 py-1.5 rounded-full bg-white border border-[#e2d8c8] text-[#6a6a6a] hover:border-[#b8935f] hover:text-[#b8935f] transition-colors">{q}</button>
                ))}
              </div>
            )}
            <div ref={endRef} />
          </div>

          <div className="p-3 border-t border-[#f0ece5] bg-white">
            {panel === "viewing" && (
              <form onSubmit={submitViewing} className="mb-3 space-y-2 p-3 rounded-lg bg-[#faf7f2] border border-[#e2d8c8]" data-testid="ai-dock-viewing-form">
                <p className="text-[12px] font-500 text-[#2b2b2b] flex items-center gap-1.5"><Home size={13} className="text-[#b8935f]" /> Book a viewing</p>
                <select value={vf.propertyId} onChange={(e) => setVf({ ...vf, propertyId: e.target.value })} data-testid="dock-viewing-property" className="w-full h-9 px-2 rounded-md border border-[#e2d8c8] text-[13px] bg-white outline-none focus:border-[#b8935f]"><option value="">Select property…</option>{props.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}</select>
                <div className="grid grid-cols-2 gap-2">
                  <input value={vf.name} onChange={(e) => setVf({ ...vf, name: e.target.value })} placeholder="Applicant name" data-testid="dock-viewing-name" className="h-9 px-2 rounded-md border border-[#e2d8c8] text-[13px] outline-none focus:border-[#b8935f]" />
                  <input value={vf.phone} onChange={(e) => setVf({ ...vf, phone: e.target.value })} placeholder="Phone" className="h-9 px-2 rounded-md border border-[#e2d8c8] text-[13px] outline-none focus:border-[#b8935f]" />
                </div>
                <input type="email" value={vf.email} onChange={(e) => setVf({ ...vf, email: e.target.value })} placeholder="Email" data-testid="dock-viewing-email" className="w-full h-9 px-2 rounded-md border border-[#e2d8c8] text-[13px] outline-none focus:border-[#b8935f]" />
                <input type="datetime-local" value={vf.preferredDate} onChange={(e) => setVf({ ...vf, preferredDate: e.target.value })} data-testid="dock-viewing-date" className="w-full h-9 px-2 rounded-md border border-[#e2d8c8] text-[13px] text-[#4a4a4a] outline-none focus:border-[#b8935f]" />
                <div className="flex gap-2"><button type="submit" disabled={saving} data-testid="dock-viewing-save" className="flex-1 h-9 rounded-md bg-[#b8935f] text-white text-[13px] font-500 disabled:opacity-60">{saving ? "Booking…" : "Book viewing"}</button><button type="button" onClick={() => setPanel(null)} className="px-3 h-9 rounded-md border border-[#e2d8c8] text-[13px] text-[#6a6a6a]">Cancel</button></div>
              </form>
            )}
            {panel === "event" && (
              <form onSubmit={submitEvent} className="mb-3 space-y-2 p-3 rounded-lg bg-[#faf7f2] border border-[#e2d8c8]" data-testid="ai-dock-event-form">
                <p className="text-[12px] font-500 text-[#2b2b2b] flex items-center gap-1.5"><CalendarPlus size={13} className="text-[#b8935f]" /> Add calendar event</p>
                <input value={ef.title} onChange={(e) => setEf({ ...ef, title: e.target.value })} placeholder="Event title" data-testid="dock-event-title" className="w-full h-9 px-2 rounded-md border border-[#e2d8c8] text-[13px] outline-none focus:border-[#b8935f]" />
                <div className="grid grid-cols-2 gap-2">
                  <input type="date" value={ef.date} onChange={(e) => setEf({ ...ef, date: e.target.value })} data-testid="dock-event-date" className="h-9 px-2 rounded-md border border-[#e2d8c8] text-[13px] text-[#4a4a4a] outline-none focus:border-[#b8935f]" />
                  <input type="time" value={ef.time} onChange={(e) => setEf({ ...ef, time: e.target.value })} className="h-9 px-2 rounded-md border border-[#e2d8c8] text-[13px] text-[#4a4a4a] outline-none focus:border-[#b8935f]" />
                </div>
                <select value={ef.type} onChange={(e) => setEf({ ...ef, type: e.target.value })} className="w-full h-9 px-2 rounded-md border border-[#e2d8c8] text-[13px] bg-white outline-none focus:border-[#b8935f]"><option value="task">Task</option><option value="viewing">Viewing</option><option value="chase">Chase / follow-up</option><option value="payment">Payment</option></select>
                <div className="flex gap-2"><button type="submit" disabled={saving} data-testid="dock-event-save" className="flex-1 h-9 rounded-md bg-[#b8935f] text-white text-[13px] font-500 disabled:opacity-60">{saving ? "Adding…" : "Add event"}</button><button type="button" onClick={() => setPanel(null)} className="px-3 h-9 rounded-md border border-[#e2d8c8] text-[13px] text-[#6a6a6a]">Cancel</button></div>
              </form>
            )}
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <button type="button" onClick={draftSocial} disabled={busy} data-testid="ai-dock-draft-social" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#faf7f2] border border-[#e2d8c8] text-[12px] text-[#6a6a6a] hover:border-[#b8935f] hover:text-[#b8935f] transition-colors disabled:opacity-60"><Share2 size={13} /> Draft social post</button>
              <button type="button" onClick={listingStats} disabled={busy} data-testid="ai-dock-stats" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#faf7f2] border border-[#e2d8c8] text-[12px] text-[#6a6a6a] hover:border-[#b8935f] hover:text-[#b8935f] transition-colors disabled:opacity-60"><BarChart3 size={13} /> Listing stats</button>
              <button type="button" onClick={() => setPanel(panel === "viewing" ? null : "viewing")} data-testid="ai-dock-book-viewing" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#faf7f2] border border-[#e2d8c8] text-[12px] text-[#6a6a6a] hover:border-[#b8935f] hover:text-[#b8935f] transition-colors"><Home size={13} /> Book viewing</button>
              <button type="button" onClick={() => setPanel(panel === "event" ? null : "event")} data-testid="ai-dock-add-event" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#faf7f2] border border-[#e2d8c8] text-[12px] text-[#6a6a6a] hover:border-[#b8935f] hover:text-[#b8935f] transition-colors"><CalendarPlus size={13} /> Add event</button>
            </div>
            <div className="flex items-center gap-2">
              <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); ask(); } }} placeholder="Ask, command, or type a post topic…" data-testid="ai-dock-input" className="flex-1 h-11 px-3 rounded-lg border border-[#e2d8c8] text-[14px] outline-none focus:border-[#b8935f]" />
              <button type="button" onClick={() => ask()} disabled={busy} data-testid="ai-dock-send" className="w-11 h-11 rounded-lg bg-[#b8935f] text-white flex items-center justify-center hover:bg-[#a67f4d] disabled:opacity-60"><Send size={16} /></button>
            </div>
          </div>
        </div>
      )}

      <button
        onClick={() => setOpen((o) => !o)}
        aria-label="Anadear AI assistant"
        data-testid="ai-dock-toggle"
        className={`fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full flex items-center justify-center shadow-[0_10px_28px_rgba(184,147,95,0.55)] transition-all duration-300 ${open ? "bg-[#1c1c1e]" : "bg-gradient-to-br from-[#b8935f] to-[#a67f4d] hover:scale-105"}`}
      >
        {open ? <X size={22} className="text-white" /> : <Bot size={24} className="text-white" />}
      </button>
    </>
  );
};

export default AiDock;
